import { arrotonda, dec, somma, ZERO, type Dec } from './dec'
import { mesiDelPeriodo, type DatiPeriodo } from './ingresso'
import type { ModelloDichiarazione, Riga, RigaMutabile } from './modello'
import type { RegolePeriodo } from './regole-periodo'

const positivo = (v: Dec | undefined): Dec | undefined => (v !== undefined && v.gt(0) ? v : undefined)
const opposto = (v: Dec): Dec => ZERO.minus(v)

function riepilogoQ(modello: ModelloDichiarazione, importi: number): RigaMutabile[] {
  const conAccisa = [
    ...(modello.E ?? []).map((riga) => ({ quadro: 'E' as const, riga })),
    ...(modello.M ?? []).map((riga) => ({ quadro: 'M' as const, riga })),
  ]
  const chiave = (r: Riga): string => `${String(r.ambito)}|${String(r.mese)}`

  return [...new Set(conAccisa.map(({ riga }) => chiave(riga)))].map((k) => {
    const gruppo = conAccisa.filter(({ riga }) => chiave(riga) === k)
    const totale = (quadro: 'E' | 'M'): Dec =>
      arrotonda(somma(...gruppo.filter((x) => x.quadro === quadro).map((x) => dec(x.riga.accisa ?? 0))), importi)
    const totE = totale('E')
    const totM = totale('M')
    const primo = gruppo[0]!.riga
    return {
      ambito: primo.ambito,
      mese: primo.mese,
      totE: totE.isZero() ? undefined : totE,
      totM: totM.isZero() ? undefined : totM,
      totaleAccisaDovuta: arrotonda(totE.plus(totM), importi),
    }
  })
}

/**
 * Versamenti mese per mese (Circ. ADM 20/2026): il versato di un mese e' il
 * dovuto del mese precedente, il primo mese usa l'acconto. Nessuna
 * compensazione fra mesi oltre al credito dichiarato come utilizzato.
 */
function versamentiS(q: readonly RigaMutabile[], dati: DatiPeriodo, regole: RegolePeriodo): RigaMutabile[] {
  const importi = regole.arrotondamento.importi
  const qs = dati.quadroS
  const inizio = dati.formulePersonalizzate.meseInizioAttivita
  const mesi = mesiDelPeriodo(dati.periodo, dati.annoImposta)
    .mesi.map((m) => m.mese)
    .filter((m) => inizio === undefined || m >= inizio)

  const righe: RigaMutabile[] = []
  for (const ambito of new Set(q.map((r) => Number(r.ambito)))) {
    const dovutaDi = (mese: number): Dec => {
      const r = q.find((x) => Number(x.ambito) === ambito && Number(x.mese) === mese)
      return r?.totaleAccisaDovuta === undefined ? ZERO : dec(r.totaleAccisaDovuta)
    }

    let precedente = qs.acconto ?? ZERO
    let totDovuta = ZERO
    let totVersata = ZERO
    let totCred = ZERO
    const mensili: RigaMutabile[] = []
    for (const mese of mesi) {
      const dovuta = dovutaDi(mese)
      const credito = qs.creditoUtilizzato?.[mese] ?? ZERO
      const daVersare = precedente.minus(credito)
      const versata = qs.versatoOverride?.[mese] ?? (daVersare.isNegative() ? ZERO : daVersare)
      const saldo = arrotonda(versata.plus(credito).minus(dovuta), importi)
      mensili.push({
        ambito,
        mese,
        impDovuta: dovuta,
        credUtilizzato: positivo(credito),
        impVersata: versata,
        credMaturatoMese: positivo(saldo),
        debMaturatoMese: saldo.isNegative() ? opposto(saldo) : undefined,
      })
      totDovuta = totDovuta.plus(dovuta)
      totVersata = totVersata.plus(versata)
      totCred = totCred.plus(credito)
      precedente = dovuta
    }

    const fine = arrotonda(
      (qs.creditoInizio ?? ZERO).minus(qs.debitoInizio ?? ZERO).plus(totVersata).minus(totDovuta),
      importi,
    )
    righe.push(
      {
        ambito,
        acconto: positivo(qs.acconto),
        creditoInizio: positivo(qs.creditoInizio),
        debitoInizio: positivo(qs.debitoInizio),
        creditoFine: positivo(fine),
        debitoFine: fine.isNegative() ? opposto(fine) : undefined,
        totImpDovuta: totDovuta,
        totCred: positivo(totCred),
        totImpVersata: positivo(totVersata),
      },
      ...mensili,
    )
  }
  return righe
}

/** Q e S esistono solo se c'e' accisa: rinnovabili uso esente e annuale non li hanno. */
export function liquida(modello: ModelloDichiarazione, dati: DatiPeriodo, regole: RegolePeriodo): ModelloDichiarazione {
  const q = riepilogoQ(modello, regole.arrotondamento.importi)
  if (q.length === 0) return modello
  return { ...modello, Q: q, S: versamentiS(q, dati, regole) }
}
